import React, { JSX, useRef } from 'react';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { TwoColContainer } from '@/components/atoms/TwoColContainer/TwoColContainer';
import { Image } from '@/components/atoms/Image/Image';
import { Typography } from '@/components/atoms/Typography/Typography';
import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ICommonProps, TImageAsset, TTextMultilanguage } from '@/types/global';
import { IconPlus } from '@/components/atoms/AnimatedIcons/IconPlus/IconPlus';
import * as Styled from './BannerInfoList.styled';
import { scrollToElement } from '@/utils/generic';

gsap.registerPlugin(ScrollSmoother);

export interface IBannerInfoColumn {
  text?: string;
  description?: string;
  image?: TImageAsset | undefined;
}

export interface IBannerInfoRow {
  columns: IBannerInfoColumn[];
}

export interface IBannerInfoList extends ICommonProps {
  title: TTextMultilanguage;
  rows: IBannerInfoRow[];
  closingText?: TTextMultilanguage;
}

/** Renders a component with a banner layout and 3 rows of information
 * @param  {IBannerInfoList} props
 * @return {JSX.Element}
 **/
export const BannerInfoList = ({
  title,
  rows = [],
  closingText
}: IBannerInfoList): JSX.Element => {
  const refRowsContainer = useRef<HTMLDivElement>(null);
  const [openedRowNumber, setOpenedRowNumber] = React.useState<number | null>(
    null
  );

  // useLayoutEffect(() => {
  //   const firstSecondRow = refRowsContainer.current?.querySelector(
  //     '.second-row'
  //   ) as HTMLElement;

  //   if (firstSecondRow) {
  //     const elHeight = firstSecondRow.scrollHeight;
  //   }
  // });

  const isOnlyText = (columns: IBannerInfoColumn[]) => {
    let isOnlyText = true;

    columns.forEach((column) => {
      if (column.image || column.description) {
        isOnlyText = false;
      }
    });

    return isOnlyText;
  };

  const openSecondRow = (
    e: React.MouseEvent<HTMLDivElement>,
    numRow: number
  ) => {
    const rowEl = e.currentTarget;
    const currentSecondRow = rowEl.querySelector('.second-row') as HTMLElement;

    // Closes the row if it is already opened
    if (openedRowNumber === numRow) {
      gsap.to(currentSecondRow, {
        duration: 0.3,
        height: 0,
        ease: 'power2.out',
      });
      setOpenedRowNumber(null);
      return;
    }

    // Closes the previous row if it is opened
    const prevRowEl = refRowsContainer.current?.querySelector(
      `[data-is-opened="true"]`
    ) as HTMLElement;

    if (prevRowEl) {
      gsap.to(prevRowEl, {
        duration: 0.3,
        height: 0,
        ease: 'power2.out',
      });
      prevRowEl.setAttribute('data-is-opened', 'false');
    }

    // Opens the new row
    const newHeight = currentSecondRow.scrollHeight;

    gsap.to(currentSecondRow, {
      duration: 0.3,
      height: newHeight,
      ease: 'power2.out',
      onStart: () => {
        currentSecondRow.setAttribute('data-is-opened', 'true');
          scrollToElement({
            target: currentSecondRow,
            smooth: true,
            scrollToTop: true,
            extraHeightUnderHeader: 65
        })
      },
      onComplete: () => {
        setOpenedRowNumber(numRow);
        // scrollToRef(currentSecondRow);
      },
    });
  };

  return (
    <ComponentContainer
      width={'main'}
      marginBottom={'default'}
      isProject={false}
      animationContainer={{
        type: 'fadeInAndUp',
      }}
    >
      <TwoColContainer
        width={'main'}
        borderTop={false}
        paddingTop={'none'}
        sideTitle={title}
      >
        <Styled.RowsContainer ref={refRowsContainer}>
          {rows.map((row, index) => row && (
            <Styled.Row key={index} onClick={(e) => openSecondRow(e, index)}>
              <Styled.InnerRow
                isOnlyText={isOnlyText(row.columns)}
                className="first-row"
              >
                {row.columns.map((column, index) => (
                  <Styled.Column key={index}>
                    {column.text && <Typography text={column.text} />}
                  </Styled.Column>
                ))}
                {!isOnlyText(row.columns) && (
                  <IconPlus
                    isExpanded={openedRowNumber === index}
                    className="icon-plus"
                  />
                )}
              </Styled.InnerRow>
              {!isOnlyText(row.columns) && (
                <Styled.InnerRow
                  isOnlyText={false}
                  className="second-row"
                  data-is-opened={openedRowNumber === index}
                  openedInitial={index === 0}
                >
                  {row.columns.map((column, index) => (
                    <Styled.Column key={index}>
                      {column.description && (
                        <Typography
                          text={column.description}
                          className="description"
                        />
                      )}
                      {column.image && (
                        <Image
                          src={column.image.src}
                          alt={column.image.alt}
                          width={290}
                          height={290}
                          includeContainer={true}
                          aspectRatio="1"
                          lazy={true}
                        />
                      )}
                    </Styled.Column>
                  ))}
                </Styled.InnerRow>
              )}
            </Styled.Row>
          ))}
          {closingText && (
            <Typography
              text={closingText}
              className="closing-text"
            />
          )}
        </Styled.RowsContainer>
      </TwoColContainer>
    </ComponentContainer>
  );
};
